<template>
    <kt-modal 
        :visible="visible" 
        title="Manage Request Types"
        size="lg"
        @dismiss="$emit('close')"
    >
        <template #body>
            <div class="request-type-selector">
                <!-- Add New Request Type Form (Admin only) -->
                <div v-if="props.canManage" class="card mb-3">
                    <div class="card-header py-2">
                        <strong>{{ editingRequestType ? 'Edit Request Type' : 'Add New Request Type' }}</strong>
                    </div>
                    <div class="card-body py-2">
                        <div class="row g-2">
                            <div class="col-md-10">
                                <input 
                                    type="text" 
                                    class="form-control form-control-sm" 
                                    v-model="newRequestType.name"
                                    placeholder="Request Type Name"
                                />
                            </div>
                            <div class="col-md-2">
                                <button 
                                    type="button"
                                    class="btn btn-sm btn-primary w-100"
                                    @click="saveRequestType"
                                    :disabled="!newRequestType.name"
                                >
                                    <Save :size="14" class="me-1" />
                                    {{ editingRequestType ? 'Update' : 'Add' }}
                                </button>
                            </div>
                        </div>
                        <div v-if="editingRequestType" class="mt-2">
                            <button 
                                type="button"
                                class="btn btn-sm btn-outline-secondary"
                                @click="cancelEdit"
                            >
                                Cancel Edit
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Request Type List -->
                <div class="card">
                    <div class="card-header py-2">
                        <strong>Select a Request Type</strong>
                    </div>
                    <div class="card-body p-0">
                        <div v-if="loading" class="text-center py-3">
                            <span class="spinner-border spinner-border-sm"></span>
                            Loading request types...
                        </div>
                        <div v-else-if="requestTypes.length === 0" class="text-center py-3 text-muted">
                            No request types configured. Add one above.
                        </div>
                        <table v-else class="table table-sm table-hover mb-0">
                            <thead>
                                <tr>
                                    <th>Request Type Name</th>
                                    <th v-if="props.canManage" style="width: 120px">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr 
                                    v-for="type in requestTypes" 
                                    :key="type.name"
                                    :class="{ 'table-primary': selectedRequestType === type.name }"
                                    style="cursor: pointer;"
                                    @click="selectRequestType(type)"
                                >
                                    <td>{{ type.name }}</td>
                                    <td v-if="props.canManage">
                                        <button 
                                            type="button"
                                            class="btn btn-sm btn-outline-primary me-1"
                                            @click.stop="editRequestType(type)"
                                            title="Edit"
                                        >
                                            <Edit :size="14" />
                                        </button>
                                        <button 
                                            type="button"
                                            class="btn btn-sm btn-outline-danger"
                                            @click.stop="deleteRequestType(type)"
                                            title="Delete"
                                        >
                                            <Trash2 :size="14" />
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </template>

        <template #footer>
            <button type="button" class="btn btn-secondary" @click="$emit('close')">
                Cancel
            </button>
            <button 
                type="button" 
                class="btn btn-primary"
                @click="confirmSelection"
                :disabled="!selectedRequestType"
            >
                Select Request Type
            </button>
        </template>
    </kt-modal>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue';
import KtModal from '@/components/UI/KtModal.vue';
import OptionsService from '@/services/OptionsService';
import Alert from '@/services/Alert';
import Toast from '@/services/Toast';
import { Save, Edit, Trash2 } from 'lucide-vue-next';

interface RequestType {
    name: string;
}

const props = defineProps<{
    visible: boolean;
    currentValue?: string | null;
    canManage?: boolean;
}>();

const emit = defineEmits<{
    (e: 'close'): void;
    (e: 'select', requestType: RequestType): void;
    (e: 'request-types-changed', requestTypes: RequestType[]): void;
}>();

const loading = ref(false);
const requestTypes = ref<RequestType[]>([]);
const selectedRequestType = ref<string | null>(null);
const editingRequestType = ref<RequestType | null>(null);
const newRequestType = ref<RequestType>({ name: '' });

const OPTION_KEY = 'request_types_json';

const loadRequestTypes = async () => {
    loading.value = true;
    try {
        const optionValue = await OptionsService.get(OPTION_KEY);
        if (optionValue) {
            requestTypes.value = JSON.parse(optionValue);
        } else {
            requestTypes.value = [];
        }
    } catch (error) {
        console.error('Error loading request types:', error);
        requestTypes.value = [];
    } finally {
        loading.value = false;
    }
};

const saveRequestTypesToOptions = async () => {
    try {
        await OptionsService.set(OPTION_KEY, JSON.stringify(requestTypes.value));
        emit('request-types-changed', requestTypes.value);
    } catch (error) {
        console.error('Error saving request types:', error);
        Toast.error('Failed to save request types');
    }
};

const saveRequestType = async () => {
    if (!newRequestType.value.name.trim()) {
        Toast.error('Request type name is required');
        return;
    }

    if (editingRequestType.value) {
        // Update existing
        const index = requestTypes.value.findIndex(t => t.name === editingRequestType.value!.name);
        if (index !== -1) {
            requestTypes.value[index] = { ...newRequestType.value };
        }
        editingRequestType.value = null;
    } else {
        // Check for duplicate
        if (requestTypes.value.some(t => t.name.toLowerCase() === newRequestType.value.name.toLowerCase())) {
            Toast.error('A request type with this name already exists');
            return;
        }
        // Add new
        requestTypes.value.push({ ...newRequestType.value });
    }

    await saveRequestTypesToOptions();
    newRequestType.value = { name: '' };
    Toast.success('Request type saved');
};

const editRequestType = (type: RequestType) => {
    editingRequestType.value = type;
    newRequestType.value = { ...type };
};

const cancelEdit = () => {
    editingRequestType.value = null;
    newRequestType.value = { name: '' };
};

const deleteRequestType = async (type: RequestType) => {
    Alert.confirm(
        `Are you sure you want to delete "${type.name}"?`,
        async () => {
            requestTypes.value = requestTypes.value.filter(t => t.name !== type.name);
            await saveRequestTypesToOptions();
            Toast.success('Request type deleted');
            
            if (selectedRequestType.value === type.name) {
                selectedRequestType.value = null;
            }
        },
        () => {}
    );
};

const selectRequestType = (type: RequestType) => {
    selectedRequestType.value = type.name;
};

const confirmSelection = () => {
    const type = requestTypes.value.find(t => t.name === selectedRequestType.value);
    if (type) {
        emit('select', type);
    }
    emit('close');
};

watch(() => props.visible, (newVal) => {
    if (newVal) {
        loadRequestTypes();
        selectedRequestType.value = props.currentValue || null;
    }
});

onMounted(() => {
    if (props.visible) {
        loadRequestTypes();
        selectedRequestType.value = props.currentValue || null;
    }
});
</script>

<style scoped>
.request-type-selector {
    min-height: 300px;
}
</style>
